import {
    useForm,
    Controller,
    type SubmitHandler } from "react-hook-form"
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION, DISCOUNT_TYPE_LABEL_MAP } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import FormRow from "@/components/forms/reusables/form-row"
import useFormStates from "@/hooks/use-form-states"
import {
    DiscountType,
    type YearlyFeeDiscount,
    type YearlyFeeDiscountFormInput,
} from "@/types"
import {
    useCreateYearlyFeeDiscount,
    useUpdateYearlyFeeDiscount,
} from "@/hooks/api/use-yearly-fee-discount"
import RHFNumberTextfield from "@/components/form-fields/rhf-number-textfield"
import RHFTextfield from "@/components/form-fields/rhf-textfield"
import { convertFromCents } from "@/helpers"

const YearlyFeeDiscountForm = ({
    yearlyFeeDiscount,
    year,
    closeCallback,
} : {
    yearlyFeeDiscount?: YearlyFeeDiscount,
    year: number | string,
    closeCallback?: () => void,
}) => {

    const formDataId = `yearlyFeeDiscountForm-${yearlyFeeDiscount?.id}-v${APP_VERSION}`

    const initialValues = {
        discount_type: yearlyFeeDiscount ? yearlyFeeDiscount.type : DiscountType.household,
        name: yearlyFeeDiscount ? yearlyFeeDiscount.name : '',
        description: yearlyFeeDiscount ? yearlyFeeDiscount.description : '',
        amount: yearlyFeeDiscount ? convertFromCents(yearlyFeeDiscount.amount_cents) : '',
        age_from: yearlyFeeDiscount ? yearlyFeeDiscount.age_from : '',
        age_to: yearlyFeeDiscount ? yearlyFeeDiscount.age_to : '',
        set_amount_to_zero: yearlyFeeDiscount ? yearlyFeeDiscount.set_amount_to_zero : false,
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        watch,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        setError,
        error,
    } = useFormStates(handleFormReset)

    const mutationCreate = useCreateYearlyFeeDiscount(
        year,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const mutationUpdate = useUpdateYearlyFeeDiscount(
        yearlyFeeDiscount?.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
        },
    )

    const onSubmit: SubmitHandler<YearlyFeeDiscountFormInput> = data => {
        setRequestInProgress(true)
        if (yearlyFeeDiscount?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    const selectedDiscountType = watch('discount_type')
    const selectedSetAmountToZero = watch('set_amount_to_zero')

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={yearlyFeeDiscount ? 'Upravit slevu' : 'Vytvořit slevu'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={yearlyFeeDiscount ? 'Upravit' : 'Vytvořit slevu'}
            error={error}
            setError={setError}
        >
            <FormControl fullWidth>
                <InputLabel id="year-select-label">Typ slevy</InputLabel>
                <Controller
                    name="discount_type"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Typ slevy">
                            <MenuItem value={DiscountType.age}>{DISCOUNT_TYPE_LABEL_MAP[DiscountType.age]}</MenuItem>
                            <MenuItem value={DiscountType.household}>{DISCOUNT_TYPE_LABEL_MAP[DiscountType.household]}</MenuItem>
                            <MenuItem value={DiscountType.householdMember}>{DISCOUNT_TYPE_LABEL_MAP[DiscountType.householdMember]}</MenuItem>
                            <MenuItem value={DiscountType.inMotivationProgram}>{DISCOUNT_TYPE_LABEL_MAP[DiscountType.inMotivationProgram]}</MenuItem>
                        </Select>
                    )}
                />
            </FormControl>
            <RHFTextfield
                name="name"
                control={control}
                label="Název slevy"
            />
            <RHFNumberTextfield
                name="amount"
                control={control}
                label="Výše slevy"
                endAdornment="Kč"
                decimalScale={2}
                thousandSeparator
                disallowNegative
                required={!selectedSetAmountToZero}
                disabled={selectedSetAmountToZero}
            />
            <Box mx="auto" mb={1}>
                <FormControlLabel control={
                    <Controller
                        name="set_amount_to_zero"
                        control={control}
                        render={({ field }) => <Switch
                            {...field}
                            value={!!field.value}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                        />}
                    />
                } label="Úplné osvobození od poplatku" />
            </Box>
            {selectedDiscountType === DiscountType.age &&
                <FormRow>
                    <RHFNumberTextfield
                        name="age_from"
                        control={control}
                        label="Věk od"
                        endAdornment="let"
                        decimalScale={0}
                        thousandSeparator
                        disallowNegative
                    />
                    <RHFNumberTextfield
                        name="age_to"
                        control={control}
                        label="Věk do"
                        endAdornment="let"
                        decimalScale={0}
                        thousandSeparator
                        disallowNegative
                    />
                </FormRow>
            }
            <RHFTextfield
                name="description"
                control={control}
                label="Popis slevy"
            />
        </Form>
    )
}

export default YearlyFeeDiscountForm
